import { CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface PersonOption {
  id: string;
  name: string;
  hint?: string;
}

interface Props {
  options: PersonOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  id?: string;
  disabled?: boolean;
}

/** Multi-select of people with search; the chosen ones show as removable chips. */
export function PeoplePicker({ options, value, onChange, placeholder = 'Add people…', emptyText = 'Nobody matches.', id, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const chosen = value.map((v) => byId.get(v)).filter((x): x is PersonOption => !!x);
  const toggle = (pid: string) => onChange(value.includes(pid) ? value.filter((x) => x !== pid) : [...value, pid]);

  return (
    <div className="grid gap-2">
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((p) => (
            <Badge key={p.id} variant="secondary" className="gap-1 pr-1">
              {p.name}
              <button type="button" aria-label={`Remove ${p.name}`} className="hover:text-destructive" onClick={() => toggle(p.id)} disabled={disabled}>
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button id={id} type="button" variant="outline" size="sm" role="combobox" aria-expanded={open} disabled={disabled || options.length === 0} className="w-fit font-normal">
            <PlusIcon aria-hidden /> {options.length === 0 ? 'No one to add' : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by name or email…" />
            <CommandList className="max-h-64">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((o) => {
                  const on = value.includes(o.id);
                  return (
                    <CommandItem key={o.id} value={`${o.name} ${o.hint ?? ''}`} onSelect={() => toggle(o.id)} className="gap-2">
                      <span className={cn('flex size-4 shrink-0 items-center justify-center border', on ? 'bg-primary border-primary text-primary-foreground' : 'border-input')} aria-hidden>
                        {on && <CheckIcon className="size-3" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {o.name}
                        {o.hint && <span className="text-muted-foreground block truncate text-xs">{o.hint}</span>}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
